import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Volume2, Loader2, Sparkles, Trash2, Quote, MessageSquare, Phone } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const TYPE_COLORS = {
  eroeffnung: "from-blue-500/20 border-blue-500/40 text-blue-300",
  bedarfsermittlung: "from-cyan-500/20 border-cyan-500/40 text-cyan-300",
  nutzenargumentation: "from-novatus-green/20 border-novatus-green/40 text-novatus-green",
  einwandbehandlung: "from-amber-500/20 border-amber-500/40 text-amber-300",
  abschluss: "from-purple-500/20 border-purple-500/40 text-purple-300",
  empathie: "from-pink-500/20 border-pink-500/40 text-pink-300",
};
const TYPE_LABEL = {
  eroeffnung: "Eröffnung",
  bedarfsermittlung: "Bedarfsermittlung",
  nutzenargumentation: "Nutzenargumentation",
  einwandbehandlung: "Einwandbehandlung",
  abschluss: "Abschluss",
  empathie: "Empathie",
};

const RecordingDetail = () => {
  const { recordingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const isAdmin = user?.role === "admin" || user?.role === "trainer";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/recordings/${recordingId}`);
        setData(data);
      } catch { toast.error("Mitschnitt nicht gefunden"); navigate("/highlights"); }
      finally { setLoading(false); }
    })();
  }, [recordingId, navigate]);

  const play = async (messageId, text) => {
    if (audioRef.current) audioRef.current.pause();
    if (playingId === messageId) { setPlayingId(null); return; }
    setPlayingId(messageId);
    try {
      const { data } = await api.post("/tts", { text });
      if (data.audio_base64) {
        const audio = new Audio("data:audio/mp3;base64," + data.audio_base64);
        audioRef.current = audio;
        audio.onended = () => setPlayingId(null);
        await audio.play();
      }
    } catch { toast.error("Audio fehlgeschlagen"); setPlayingId(null); }
  };

  const playAllHighlights = async () => {
    if (!data?.recording?.highlights?.length) return;
    for (const h of data.recording.highlights) {
      const msg = data.messages[h.message_index];
      if (!msg) continue;
      await new Promise(async (resolve) => {
        try {
          const { data: tts } = await api.post("/tts", { text: msg.content });
          if (!tts.audio_base64) return resolve();
          const audio = new Audio("data:audio/mp3;base64," + tts.audio_base64);
          audioRef.current = audio;
          setPlayingId(msg.id);
          audio.onended = () => { setPlayingId(null); resolve(); };
          audio.onerror = () => resolve();
          await audio.play();
        } catch { resolve(); }
      });
    }
  };

  const removeRecording = async () => {
    if (!window.confirm("Mitschnitt wirklich aus der Bibliothek entfernen?")) return;
    try {
      await api.delete(`/admin/recordings/${recordingId}`);
      toast.success("Entfernt");
      navigate("/highlights");
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const scrollToMessage = (idx) => {
    const el = document.getElementById(`msg-${idx}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-novatus-green/60");
      setTimeout(() => el.classList.remove("ring-2", "ring-novatus-green/60"), 1800);
    }
  };

  if (loading) return <div className="p-12 text-center text-novatus-text-3"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return null;

  const rec = data.recording;
  const messages = data.messages;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <button onClick={() => navigate("/highlights")} className="text-sm text-novatus-text-3 hover:text-novatus-green inline-flex items-center gap-1 mb-4" data-testid="back-to-highlights-btn">
        <ArrowLeft className="w-4 h-4" /> Alle Mitschnitte
      </button>

      <div className="nv-card p-6 sm:p-8 relative overflow-hidden animate-slide-up">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          background: "radial-gradient(circle at 90% 0%, rgba(31,203,79,0.3) 0%, transparent 60%)",
        }} />
        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-novatus-green">{rec.mode === "active_call" ? "KI-Demo-Call" : "Top-Call"}</span>
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-2">Score {rec.score}</span>
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-2">{rec.scenario_title}</span>
            </div>
            <h1 className="font-display font-black text-2xl sm:text-4xl tracking-tight text-white">{rec.title}</h1>
            <p className="text-novatus-text-2 mt-3 leading-relaxed max-w-2xl">{rec.description}</p>
            <p className="text-xs text-novatus-text-3 mt-3">Verkäufer: <span className="text-white">{rec.seller_name}</span> · geteilt von <span className="text-white">{rec.shared_by_name}</span></p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={playAllHighlights} className="btn-green h-11 px-5 rounded-lg flex items-center gap-2" data-testid="play-all-highlights-btn">
              <Sparkles className="w-4 h-4" /> Alle Highlights abspielen
            </button>
            {isAdmin && (
              <button onClick={removeRecording} className="h-11 px-4 rounded-lg bg-red-500/15 border border-red-500/30 text-red-400 hover:bg-red-500/25 flex items-center gap-2" data-testid="delete-recording-btn">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
        {/* Highlights sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="nv-card p-6 lg:sticky lg:top-24" data-testid="highlights-list">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-novatus-green" />
              <h3 className="font-display font-bold text-white">{rec.highlights?.length || 0} KI-Highlights</h3>
            </div>
            <div className="space-y-2">
              {(rec.highlights || []).map((h, i) => (
                <button
                  key={`hl-${h.message_index}-${h.type || i}`}
                  onClick={() => scrollToMessage(h.message_index)}
                  className={`w-full text-left p-3 rounded-lg bg-gradient-to-br ${TYPE_COLORS[h.type] || TYPE_COLORS.empathie} bg-novatus-card-2 border hover:scale-[1.01] transition-transform`}
                  data-testid={`highlight-${i}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wider font-bold">{TYPE_LABEL[h.type] || h.type}</span>
                    <span className="text-[10px] text-novatus-text-3">#{h.message_index + 1}</span>
                  </div>
                  <p className="text-sm font-semibold text-white leading-tight">{h.title}</p>
                  <p className="text-xs text-novatus-text-2 mt-1.5 line-clamp-2 leading-relaxed">{h.note}</p>
                </button>
              ))}
              {(rec.highlights || []).length === 0 && (
                <p className="text-sm text-novatus-text-3">Keine Highlights generiert.</p>
              )}
            </div>
          </div>
        </aside>

        {/* Transcript */}
        <section className="lg:col-span-8 xl:col-span-9">
          <div className="nv-card overflow-hidden">
            <div className="border-b border-novatus px-5 py-3 flex items-center gap-2">
              {rec.mode === "active_call" ? <Phone className="w-4 h-4 text-novatus-green" /> : <MessageSquare className="w-4 h-4 text-novatus-green" />}
              <h3 className="font-display font-bold text-white">Transkript</h3>
              <span className="text-xs text-novatus-text-3 ml-auto">{messages.length} Nachrichten</span>
            </div>
            <div className="px-4 sm:px-6 py-5 space-y-3 max-h-[60vh] overflow-y-auto" data-testid="transcript">
              {messages.map((m, i) => {
                const isHighlighted = rec.highlights?.some(h => h.message_index === i);
                const sellerSide = (rec.mode === "active_call" && m.role === "assistant") ||
                                   (rec.mode !== "active_call" && m.role === "user");
                return (
                  <div
                    key={m.id || i}
                    id={`msg-${i}`}
                    className={`flex ${sellerSide ? "justify-end" : "justify-start"} transition-shadow rounded-2xl`}
                  >
                    <div className="max-w-[82%] flex items-end gap-2">
                      <div className={`group px-4 py-3 rounded-2xl text-sm leading-relaxed relative ${
                        sellerSide ? "bg-novatus-green text-black rounded-br-sm" : "bubble-ai rounded-bl-sm"
                      } ${isHighlighted ? "ring-2 ring-novatus-green/70" : ""}`}>
                        <div className="flex items-start justify-between gap-2">
                          <span className={`text-[10px] uppercase tracking-wider font-bold ${sellerSide ? "text-black/60" : "text-novatus-text-3"}`}>
                            {sellerSide ? "Verkäufer" : "Kunde"} · #{i + 1}
                          </span>
                          <button
                            onClick={() => play(m.id, m.content)}
                            className={`shrink-0 p-1 rounded-full ${sellerSide ? "hover:bg-black/10" : "hover:bg-white/5"}`}
                            title="Vorlesen"
                            data-testid={`play-msg-${i}`}
                          >
                            <Volume2 className={`w-3.5 h-3.5 ${playingId === m.id ? "text-red-500 animate-pulse" : sellerSide ? "text-black/70" : "text-novatus-text-2"}`} />
                          </button>
                        </div>
                        <p className="mt-1">{m.content}</p>
                        {isHighlighted && (
                          <div className="mt-2 pt-2 border-t border-black/10 text-[11px] font-semibold flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> KI-Highlight
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quotes list */}
          {rec.highlights?.length > 0 && (
            <div className="nv-card mt-5 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Quote className="w-4 h-4 text-novatus-green" />
                <h3 className="font-display font-bold text-white">Übernehmenswerte Formulierungen</h3>
              </div>
              <div className="space-y-3">
                {rec.highlights.map((h, i) => (
                  <div key={`hlq-${h.message_index}-${h.type || i}`} className="p-3 rounded-lg bg-novatus-card-2 border border-novatus">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-novatus-green">{TYPE_LABEL[h.type] || h.type}</span>
                    <p className="text-sm text-white mt-1.5 italic leading-relaxed">„{h.quote}"</p>
                    <p className="text-xs text-novatus-text-2 mt-2 leading-relaxed">{h.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default RecordingDetail;
